import type {
  AdaptiveContentCandidate,
  ExposureMix,
  ScheduledContentItem,
} from "./types";

export function calculateExposureMix(totalItems: number): ExposureMix {
  const total = Math.max(0, Math.floor(totalItems));
  if (total === 0) {
    return { target: 0, support: 0, review: 0, total: 0 };
  }

  const target = Math.max(1, Math.round(total * 0.55));
  const review = total >= 4 ? Math.max(1, Math.round(total * 0.15)) : 0;
  const support = Math.max(0, total - target - review);
  return { target, support, review, total };
}

function overlap(values: readonly string[], targets: ReadonlySet<string>): number {
  return values.filter((value) => targets.has(value)).length;
}

export function scheduleAdaptiveContent(
  candidates: readonly AdaptiveContentCandidate[],
  options: {
    totalItems: number;
    targetSkills: readonly string[];
    reviewSkills?: readonly string[];
    recentContentIds?: readonly string[];
    maximumDifficulty?: number;
  },
): ScheduledContentItem[] {
  const mix = calculateExposureMix(options.totalItems);
  const targetSkills = new Set(options.targetSkills);
  const reviewSkills = new Set(options.reviewSkills ?? []);
  const recent = new Set(options.recentContentIds ?? []);
  const maximumDifficulty = options.maximumDifficulty ?? Number.POSITIVE_INFINITY;
  const usable = candidates.filter(
    (candidate) =>
      !recent.has(candidate.id) &&
      candidate.difficulty <= maximumDifficulty &&
      candidate.text.trim().length > 0,
  );

  const rankedTargets = [...usable].sort(
    (left, right) =>
      overlap(right.targetSkills, targetSkills) -
        overlap(left.targetSkills, targetSkills) ||
      left.difficulty - right.difficulty ||
      left.id.localeCompare(right.id),
  );
  const rankedReview = [...usable].sort(
    (left, right) =>
      overlap(right.reviewSkills ?? right.targetSkills, reviewSkills) -
        overlap(left.reviewSkills ?? left.targetSkills, reviewSkills) ||
      left.id.localeCompare(right.id),
  );
  const selected = new Map<string, ScheduledContentItem>();

  const take = (
    source: readonly AdaptiveContentCandidate[],
    count: number,
    reason: ScheduledContentItem["reason"],
    predicate: (candidate: AdaptiveContentCandidate) => boolean,
  ) => {
    for (const candidate of source) {
      if (selected.size >= mix.total || count <= 0) break;
      if (selected.has(candidate.id) || !predicate(candidate)) continue;
      selected.set(candidate.id, { ...candidate, reason });
      count -= 1;
    }
  };

  take(
    rankedTargets,
    mix.target,
    "target",
    (candidate) => overlap(candidate.targetSkills, targetSkills) > 0,
  );
  take(
    rankedReview,
    mix.review,
    "review",
    (candidate) =>
      overlap(candidate.reviewSkills ?? candidate.targetSkills, reviewSkills) > 0,
  );
  take(
    usable,
    mix.support,
    "support",
    (candidate) => overlap(candidate.targetSkills, targetSkills) === 0,
  );
  take(usable, mix.total - selected.size, "support", () => true);

  return [...selected.values()];
}

